import { Link } from '@tanstack/react-router'
import { Package } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ProductSummaryItem = {
  id: string | number
  title?: string | null
  description?: string | null
  quantity?: string | number | null
  productUuid?: string | null
  imageUrl?: string | null
  manufacturerName?: string | null
  manufacturerUuid?: string | null
}

type ProductSummaryMoreHref =
  | { to: '/projects/$projectId'; params: { projectId: string } }
  | { to: '/shipments/$shipmentId'; params: { shipmentId: string } }
  | { to: '/purchase-orders/$poId'; params: { poId: string } }

function cleanProductTitle(title: string | null | undefined, manufacturerName?: string | null) {
  if (!title) return '-'
  const manufacturer = manufacturerName?.trim()
  if (!manufacturer) return title

  const lowerTitle = title.toLocaleLowerCase()
  const lowerManufacturer = manufacturer.toLocaleLowerCase()
  const byManufacturer = ` by ${lowerManufacturer}`
  const byIndex = lowerTitle.indexOf(byManufacturer)
  if (byIndex >= 0) {
    return `${title.slice(0, byIndex)} ${title.slice(byIndex + byManufacturer.length)}`.trim()
  }

  const manufacturerIndex = lowerTitle.indexOf(lowerManufacturer)
  if (manufacturerIndex >= 0) {
    return `${title.slice(0, manufacturerIndex)} ${title.slice(manufacturerIndex + manufacturer.length)}`.trim()
  }

  return title
}

export function ProductSummaryRow({
  item,
  compact = false,
  showDescription = !compact,
  linkEntities = true,
  imageClassName,
  className,
}: {
  item: ProductSummaryItem
  compact?: boolean
  showDescription?: boolean
  linkEntities?: boolean
  imageClassName?: string
  className?: string
}) {
  const title = cleanProductTitle(item.title, item.manufacturerName)
  const titleClassName = compact
    ? 'block truncate text-xs font-medium text-primary hover:underline'
    : 'block break-words text-sm font-medium text-primary hover:underline'
  const plainTitleClassName = compact
    ? 'block truncate text-xs font-medium'
    : 'block break-words text-sm font-medium'
  const secondaryClassName = compact
    ? 'block truncate text-[11px] text-muted-foreground'
    : 'block break-words text-xs text-muted-foreground'

  return (
    <div className={cn('flex min-w-0 max-w-full items-start gap-2 py-0.5', className)}>
      {item.imageUrl ? (
        <img
          src={item.imageUrl}
          alt=""
          className={cn('h-7 w-7 shrink-0 rounded border object-cover', imageClassName)}
        />
      ) : (
        <div
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded border bg-muted',
            imageClassName,
          )}
        >
          <Package className="h-3 w-3 text-muted-foreground" />
        </div>
      )}
      <div className="min-w-0 flex-1 whitespace-normal">
        {linkEntities && item.productUuid ? (
          <Link
            to="/products/$productId"
            params={{ productId: item.productUuid }}
            className={titleClassName}
          >
            {title}
          </Link>
        ) : (
          <span className={plainTitleClassName}>{title}</span>
        )}
        {showDescription && item.description && (
          <p className={secondaryClassName}>{item.description}</p>
        )}
        {item.manufacturerName && (
          <span className={secondaryClassName}>
            {linkEntities && item.manufacturerUuid ? (
              <Link
                to="/manufacturers/$manuId"
                params={{ manuId: item.manufacturerUuid }}
                className="hover:text-primary hover:underline"
              >
                {item.manufacturerName}
              </Link>
            ) : (
              item.manufacturerName
            )}
          </span>
        )}
      </div>
      {item.quantity != null && (
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">×{item.quantity}</span>
      )}
    </div>
  )
}

export function ProductSummaryList({
  items,
  moreHref,
  compact = true,
  linkEntities = true,
  className,
}: {
  items: ProductSummaryItem[]
  moreHref?: ProductSummaryMoreHref
  compact?: boolean
  linkEntities?: boolean
  className?: string
}) {
  if (!items.length) return <span className="text-xs text-muted-foreground">-</span>

  return (
    <div className={cn('min-w-0 max-w-full space-y-0.5', className)}>
      {items.slice(0, 2).map((item) => (
        <ProductSummaryRow
          key={item.id}
          item={item}
          compact={compact}
          showDescription={!compact}
          linkEntities={linkEntities}
        />
      ))}
      {items.length > 2 &&
        (moreHref ? (
          <Link
            to={moreHref.to}
            params={moreHref.params}
            className="text-xs text-muted-foreground hover:text-primary hover:underline"
          >
            +{items.length - 2} more
          </Link>
        ) : (
          <p className="text-[11px] text-muted-foreground">+{items.length - 2} more</p>
        ))}
    </div>
  )
}
